import ContactsTable from '../Components/ContactsTable';
import { useTranslation } from 'react-i18next';
import useHeader from '@hooks/useHeader';
import { Box } from '@mui/material';
import {
    DeleteOutlineRounded,
    GetAppRounded,
    GroupAddRounded,
    UploadRounded,
} from '@mui/icons-material';
import Filters from '@Components/Specific/Filters/Filters';
import { contactFilters } from '@Components/Specific/Filters/filterSets';
import { useMemo } from 'react';
import QuickOptions, { QuickOption } from '@Components/General/QuickOptions';
import useDialog from '@hooks/useDialog';
import { useMutation, useQuery, useSuspenseQuery } from '@tanstack/react-query';
import axiosInstance from '@api/axiosInstance';
import { closeOptions } from '@utils/utils';
import { saveAsCSV } from '@utils/apiUtils';
import { useLoaderDeps, useNavigate } from '@tanstack/react-router';
import {
    contactFiltersQueryOptions,
    contactsQueryOptions,
} from '@utils/queryOptions';
import usePagination from '@hooks/usePagination';
import useSort from '@hooks/useSort';
import useSearchFilters from '@hooks/useSearchFilters';
import useCheckedItems from '@hooks/useCheckedItems';
import Toolbar from '@Components/General/Toolbar';
import SavedFilters from '@Components/SavedFilters';
import api from '@api/apiServices';
import useSavedFilters from '@hooks/useSavedFilters';
import NoContactsFound from '../Components/no-contacts-found';
import useEmptySearchParams from '@hooks/useEmptySearchParams';
import NoContacts from '../Components/no-contacts';
import { contactsExportFields } from '@data/exportFields';
import AddContactToGroup from '@Components/General/dialog-content/add-contact-to-group';
import { useForm } from 'react-hook-form';
import useContactsUpload from './useContactsUpload';

const Contacts = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const dialog = useDialog();

    const { control } = useForm();

    const handleContactsUpload = useContactsUpload();

    const { checkedItems, setCheckedItems, toggleItem } = useCheckedItems();

    const addContactsToGroupMutation = useMutation({
        mutationKey: ['addContactsToGroup'],
        onSuccess: () => {
            dialog.close();
            closeOptions();
            setCheckedItems([]);
        },
    });

    const { data, refetch } = useSuspenseQuery(
        contactsQueryOptions({
            page_size: parseInt(
                localStorage.getItem('contactsPageSize') || '50',
            ),
            ...useLoaderDeps({}),
        }),
    );

    const { results: contacts, count } = data || {};

    const { page, setPage, pageCount, pageSize, setPageSize } = usePagination(
        count,
        'contacts',
    );

    const { getSort, setSort } = useSort();

    const {
        filters: filterParams,
        addFilter: addParam,
        exportFilters,
        removeFilter: removeParam,
        setFilter,
    } = useSearchFilters();

    const searchParamsEmpty = useEmptySearchParams();

    const { data: savedFilters, refetch: refetchFilters } = useSuspenseQuery(
        contactFiltersQueryOptions(),
    );

    const savedFiltersHook = useSavedFilters({
        savedFilters,
        refetchFilters,
        mutationKey: 'contactFilter',
    });

    const headerProps = useMemo(() => {
        return {
            title: t('Contacts'),
            // quickActions: [
            //     {
            //         label: t('Upload'),
            //         action: () => console.log('Upload Contacts'),
            //         startIcon: <FileUploadRounded />,
            //     },
            // ],
            fabAction: () => () => navigate({ to: 'new/edit' }),
            searchProps: {
                searchPlacholder: t('search:contacts'),
            },
        };
    }, []);

    useHeader(headerProps);

    const deleteContactsMutation = useMutation({
        mutationFn: () =>
            axiosInstance.delete('/contacts', { data: checkedItems }),
        onSuccess: () => {
            refetch();
            setCheckedItems([]);
            dialog.close();
            closeOptions();
        },
    });

    const { refetch: exportContacts } = useQuery({
        enabled: false,
        queryKey: ['exportContacts', exportFilters],
        queryFn: () => {
            api.contacts
                .get({ ...exportFilters, contact_id__in: checkedItems })
                .then(res => {
                    saveAsCSV(res, {
                        name: 'contacts',
                        fields: contactsExportFields(),
                    });
                    closeOptions();
                    setCheckedItems([]);
                });
        },
    });

    const contactsOptions: QuickOption[] = [
        {
            label: t('prompts:menu.exportContacts.', {
                count: checkedItems.length,
            }),
            onClick: exportContacts,
            Icon: GetAppRounded,
            disabled: !count,
        },
        {
            label: t('Add to Group'),
            onClick: () =>
                dialog.open({
                    title: t('prompts:title.addToGroup.', {
                        count: checkedItems.length,
                    }),
                    content: <AddContactToGroup control={control} />,
                    confirmLabel: t('Add'),
                    confirmMutation: addContactsToGroupMutation,
                    onConfirm: () => ({
                        groupId: control._formValues.group.id,
                        contactIds: checkedItems,
                    }),
                }),
            Icon: GroupAddRounded,
            quickButton: true,
            disabled: !checkedItems.length,
        },
        {
            label: t('prompts:menu.importContacts'),
            Icon: UploadRounded,
            onClick: handleContactsUpload,
        },
        {
            label: t('prompts:menu.deleteContacts.', {
                count: checkedItems.length,
            }),
            onClick: () =>
                dialog.open({
                    title: t('prompts:title.deleteContacts.', {
                        count: checkedItems.length,
                    }),
                    content: t('prompts:content.deleteContacts'),
                    confirmMutation: deleteContactsMutation,
                    confirmLabel: t('Delete'),
                    confirmButtonProps: {
                        color: 'error',
                    },
                }),
            Icon: DeleteOutlineRounded,
            sx: { color: 'error.main' },
            disabled: !checkedItems.length,
        },
    ];

    if (contacts.length === 0 && searchParamsEmpty) {
        return <NoContacts />;
    }

    return (
        <Box flexGrow={1}>
            <Box display="flex" flexDirection="column" height="100%">
                <Toolbar>
                    <Box overflow="auto" display="flex" gap={1}>
                        <SavedFilters {...savedFiltersHook} />
                        <Filters
                            filterSet={contactFilters}
                            filters={filterParams}
                            addFilter={addParam}
                            // filters={{
                            //     country__icontains: ['IL', 'IL'],
                            //     full_name__icontains: ['T', 'Test', 'Test'],
                            //     description__icontains: ['Test', 'Test'],
                            //     city__icontains: ['Bocka', 'Bocka'],
                            //     state__iexact: ['Florida', 'Florida'],
                            // }}
                            removeFilter={removeParam}
                            setFilter={setFilter}
                        />
                    </Box>
                    <QuickOptions options={contactsOptions} />
                </Toolbar>
                {contacts.length ? (
                    <ContactsTable
                        contacts={contacts}
                        pageSize={pageSize}
                        setPageSize={setPageSize}
                        count={count}
                        checkedContacts={checkedItems}
                        toggleCheckItem={toggleItem}
                        setCheckedContacts={setCheckedItems}
                        getSort={getSort}
                        onSortChange={setSort}
                        pageCount={pageCount}
                        page={page}
                        setPage={setPage}
                    />
                ) : (
                    <NoContactsFound />
                )}
            </Box>
        </Box>
    );
};

export default Contacts;
